import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, Check, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Input, Select, Consent, Honeypot, validateContact, CONTACT_METHODS } from "@/components/site/Form";
import { SuccessNote } from "@/components/vehicle/Forms";
import { api, apiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

const STEPS = ["Your vehicle", "Details", "History & condition", "Photos", "Your details"];
const PHOTO_SLOTS = ["Front", "Rear", "Both sides", "Interior", "Dashboard", "Wheels", "Any damage"];
const EMPTY = { registration: "", mileage: "", make: "", model: "", derivative: "", year: "", colour: "", transmission: "", fuel: "",
  service_history: "", owners: "", keys: "", mot_expiry: "", condition: "", outstanding_finance: "", name: "", email: "", phone: "",
  postcode: "", preferred_contact: "Telephone", consent: false, website: "", photos: {} };

export default function Sell() {
  useSeo({ title: "Sell Your 4x4 in the Cotswolds | Cirencester 4x4", description: "We buy well-maintained 4x4s and SUVs directly from owners. Request a no-obligation appraisal from Cirencester 4x4.", path: "/sell-your-4x4" });
  return (
    <main data-testid="sell-page">
      <PageHero eyebrow="Sell your 4x4" title="Sell your 4x4" sub="A straightforward way to sell a good vehicle." image="/img/premises_home.webp">
        <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/80">We are always interested in well-maintained 4x4s and SUVs that suit our stock profile.</p>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-charcoal/80">If you have a vehicle you think belongs at Cirencester 4x4, send us the details and we will provide an initial appraisal.</p>
        <div className="mt-8 flex flex-wrap gap-6 text-sm font-semibold text-forest"><span className="flex items-center gap-2"><Check className="h-4 w-4" />No obligation.</span><span className="flex items-center gap-2"><Check className="h-4 w-4" />Part exchange also welcome.</span></div>
      </PageHero>
      <ValuationForm />
    </main>
  );
}

export function ValuationForm({ leadSource = "Website - Sell Your 4x4" }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ ...EMPTY, lead_source: leadSource });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const top = useRef(null);
  const bind = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), error: errors[k] });

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!f.registration.trim()) e.registration = "Please enter your registration";
      if (!f.mileage.trim()) e.mileage = "Please enter the approximate mileage";
    }
    if (step === 1 && !f.make.trim()) e.make = "Please enter the make";
    if (step === 1 && !f.model.trim()) e.model = "Please enter the model";
    if (step === 4) Object.assign(e, validateContact(f));
    setErrors(e);
    return !Object.keys(e).length;
  };
  const move = (d) => {
    if (d > 0 && !validate()) return;
    setStep((s) => s + d);
    top.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (step < 4) return move(1);
    if (!validate()) return;
    setBusy(true);
    try { await api.post("/valuations", f); setDone(true); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
      <section ref={top} id="valuation" className="mx-auto max-w-4xl scroll-mt-28 px-5 py-20 sm:px-8 lg:py-28">
        {done ? (
          <SuccessNote title="Thank you — your valuation request has been received." testid="valuation-success">We will review the details and photos and come back to you with an initial appraisal. No obligation.</SuccessNote>
        ) : (
          <>
            <ol className="mb-12 grid grid-cols-5 gap-2" aria-label="Progress">
              {STEPS.map((s, i) => (
                <li key={s} aria-current={i === step ? "step" : undefined}>
                  <div className={`h-[3px] transition-colors duration-500 ${i <= step ? "bg-forest" : "bg-stone"}`} />
                  <p className={`mt-3 hidden text-xs font-semibold uppercase tracking-[0.12em] sm:block ${i === step ? "text-forest" : "text-charcoal/60"}`}>{i + 1}. {s}</p>
                </li>
              ))}
            </ol>
            <p className="eyebrow text-forest sm:hidden">Step {step + 1} of 5 · {STEPS[step]}</p>
            <form noValidate onSubmit={submit} className="relative" data-testid="valuation-form">
              <Honeypot value={f.website} onChange={(v) => setF({ ...f, website: v })} />
              <AnimatePresence initial={false}>
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45 }}>
                  <h2 className="mb-8 mt-2 font-serif text-4xl text-charcoal">{STEPS[step]}</h2>
                  {step === 0 && (
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Input id="val-reg" label="Registration" required value={f.registration} onChange={(e) => setF({ ...f, registration: e.target.value.toUpperCase() })} error={errors.registration} hint="Vehicle lookup will pre-fill details once connected." />
                      <Input id="val-mileage" label="Mileage" required inputMode="numeric" {...bind("mileage")} />
                    </div>
                  )}
                  {step === 1 && (
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Input id="val-make" label="Make" required {...bind("make")} />
                      <Input id="val-model" label="Model" required {...bind("model")} />
                      <Input id="val-derivative" label="Derivative" {...bind("derivative")} />
                      <Input id="val-year" label="Year" inputMode="numeric" {...bind("year")} />
                      <Input id="val-colour" label="Colour" {...bind("colour")} />
                      <Select id="val-transmission" label="Transmission" options={["Automatic", "Manual"]} {...bind("transmission")} />
                      <Select id="val-fuel" label="Fuel" options={["Diesel", "Petrol", "Hybrid", "Plug-in hybrid", "Electric"]} {...bind("fuel")} />
                    </div>
                  )}
                  {step === 2 && (
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Select id="val-history" label="Service history" options={["Full main dealer", "Full specialist", "Part", "None"]} {...bind("service_history")} />
                      <Select id="val-owners" label="Number of owners" options={["1", "2", "3", "4+"]} {...bind("owners")} />
                      <Select id="val-keys" label="Number of keys" options={["1", "2", "3+"]} {...bind("keys")} />
                      <Input id="val-mot" label="MOT expiry" type="month" {...bind("mot_expiry")} />
                      <Select id="val-condition" label="Condition" options={["Excellent", "Good", "Fair", "Needs work"]} {...bind("condition")} />
                      <Select id="val-finance" label="Outstanding finance" options={["No", "Yes", "Not sure"]} {...bind("outstanding_finance")} />
                    </div>
                  )}
                  {step === 3 && <PhotoStep photos={f.photos} setPhotos={(p) => setF((x) => ({ ...x, photos: p }))} />}
                  {step === 4 && (
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Input id="val-name" label="Name" required autoComplete="name" {...bind("name")} />
                      <Input id="val-email" label="Email" type="email" required autoComplete="email" {...bind("email")} />
                      <Input id="val-phone" label="Telephone" type="tel" required autoComplete="tel" {...bind("phone")} />
                      <Input id="val-postcode" label="Postcode" autoComplete="postal-code" {...bind("postcode")} />
                      <Select id="val-method" label="Preferred contact method" options={CONTACT_METHODS} {...bind("preferred_contact")} />
                      <div className="sm:col-span-2"><Consent id="val-consent" checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} error={errors.consent} /></div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
              <div className="mt-12 flex items-center justify-between border-t border-stone pt-8">
                {step > 0 ? <button type="button" onClick={() => move(-1)} className="btn btn-secondary" data-testid="valuation-back"><ArrowLeft className="h-4 w-4" /> Back</button> : <span />}
                <button type="submit" disabled={busy} className="btn btn-primary" data-testid={step < 4 ? "valuation-next" : "valuation-submit"}>
                  {step < 4 ? <>Continue <ArrowRight className="arrow h-4 w-4" /></> : busy ? "Sending…" : "Request my valuation"}
                </button>
              </div>
            </form>
          </>
        )}
      </section>
  );
}

function PhotoStep({ photos, setPhotos }) {
  const [uploading, setUploading] = useState("");
  const [previews, setPreviews] = useState({});
  const upload = async (slot, files) => {
    if (!files?.length) return;
    setUploading(slot);
    try {
      const paths = [];
      for (const file of Array.from(files).slice(0, 4)) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/uploads/valuation", fd);
        paths.push(r.data.path);
        setPreviews((p) => ({ ...p, [slot]: [...(p[slot] || []), URL.createObjectURL(file)] }));
      }
      setPhotos({ ...photos, [slot]: [...(photos[slot] || []), ...paths] });
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(""); }
  };
  return (
    <div>
      <p className="mb-6 text-sm text-charcoal/80">Clear daylight photos help us give a more accurate appraisal. Optional, up to 4 per area, 10MB each.</p>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {PHOTO_SLOTS.map((slot) => {
          const id = `photo-${slot.toLowerCase().replace(/\s+/g, "-")}`;
          const list = previews[slot] || [];
          return (
            <label key={slot} htmlFor={id} className="group relative flex aspect-square cursor-pointer flex-col items-center justify-center overflow-hidden border border-dashed border-stone-dark bg-[#FBF9F4] text-center transition-colors hover:border-forest" data-testid={`${id}-slot`}>
              {list[0] && <img src={list[list.length - 1]} alt="" className="absolute inset-0 h-full w-full object-cover" />}
              <span className={`relative z-[1] flex flex-col items-center gap-2 p-2 ${list[0] ? "bg-paper/90 px-3 py-2" : ""}`}>
                {uploading === slot ? <Loader2 className="h-5 w-5 animate-spin text-forest" /> : list[0] ? <Check className="h-5 w-5 text-forest" /> : <Camera className="h-6 w-6 text-forest" strokeWidth={1.25} />}
                <span className="text-xs font-semibold uppercase tracking-[0.12em]">{slot}{list.length > 1 ? ` (${list.length})` : ""}</span>
              </span>
              <input id={id} type="file" accept="image/*" multiple className="sr-only" onChange={(e) => upload(slot, e.target.files)} data-testid={id} />
            </label>
          );
        })}
      </div>
    </div>
  );
}
